import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Flex,
  VStack,
  Heading,
  Text,
  Button,
  Input,
  Textarea,
  SimpleGrid,
  Icon,
  useToast
} from '@chakra-ui/react';
import { FaPhone, FaEnvelope, FaCalendarCheck, FaRocket, FaUsers } from 'react-icons/fa';
import Navigation from './Navigation';
import Footer from './Footer';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    phone: '',
    message: ''
  });
  
  const toast = useToast();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: Implement form submission logic
    console.log('Form submitted:', formData);
    toast({
      title: "Message Sent!",
      description: "We'll get back to you within 24 hours.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
    setFormData({
      name: '',
      email: '',
      organization: '',
      phone: '',
      message: ''
    });
  };

  const FeatureCard = ({ icon, title, description }) => (
    <Box
      p={6}
      bg="white"
      borderRadius="xl"
      boxShadow="sm"
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-4px)', boxShadow: 'md' }}
    >
      <Icon as={icon} w={8} h={8} color="orange.500" mb={4} />
      <Heading size="md" mb={3}>{title}</Heading>
      <Text color="gray.600">{description}</Text>
    </Box>
  );

  return (
    <Box bg="gray.50" minH="100vh">
      <Navigation />
      
      <Container maxW="container.xl" py={12}>
        {/* Hero Section */}
        <Box textAlign="center" mb={16}>
          <Heading
            as="h1"
            size="2xl"
            mb={4}
            bgGradient="linear(to-r, orange.400, orange.600)"
            bgClip="text"
          >
            Let's Create Something Amazing Together
          </Heading>
          <Text fontSize="xl" color="gray.600" maxW="2xl" mx="auto">
            Transform your sports organization's visual content with AI-powered automation and professional design tools.
          </Text>
        </Box>

        {/* Features Grid */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} mb={16}>
          <FeatureCard
            icon={FaRocket}
            title="Automated Design"
            description="AI-powered tools that automatically create branded sports content for your organization."
          />
          <FeatureCard
            icon={FaUsers}
            title="Team Management"
            description="Easily manage multiple teams, events, and photo sessions from one dashboard."
          />
          <FeatureCard
            icon={FaCalendarCheck}
            title="Event Coverage"
            description="Professional photography coverage for your games, tournaments, and special events."
          />
        </SimpleGrid>

        {/* Contact Grid */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} mb={16}>
          {/* Contact Info */}
          <VStack align="stretch" spacing={8} p={8} bg="white" borderRadius="xl" boxShadow="sm">
            <Heading size="lg">Get in Touch</Heading>
            <Text color="gray.600">
              Ready to elevate your sports organization's visual content? Schedule a free demo to see how PlayMaker Pics can help you engage athletes and parents while increasing revenue.
            </Text>
            
            <VStack align="stretch" spacing={4}>
              <Flex align="center">
                <Icon as={FaPhone} mr={4} color="orange.500" />
                <Box>
                  <Text fontWeight="bold">Call or Text</Text>
                  <Link href="tel:913-602-4887">913-602-4887</Link>
                </Box>
              </Flex>
              
              <Flex align="center">
                <Icon as={FaEnvelope} mr={4} color="orange.500" />
                <Box>
                  <Text fontWeight="bold">Email Us</Text>
                  <Link href="mailto:hello@playmakerpics.com">hello@playmakerpics.com</Link>
                </Box>
              </Flex>
            </VStack>
          </VStack>

          {/* Contact Form */}
          <Box p={8} bg="white" borderRadius="xl" boxShadow="sm">
            <form onSubmit={handleSubmit}>
              <VStack spacing={4}>
                <Input
                  placeholder="Your Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
                <Input
                  placeholder="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                <Input
                  placeholder="Organization Name"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                />
                <Input
                  placeholder="Phone Number"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                />
                <Textarea
                  placeholder="Tell us about your organization and what you're looking for..."
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                />
                <Button
                  type="submit"
                  colorScheme="orange"
                  size="lg"
                  width="full"
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: 'lg',
                  }}
                >
                  Schedule Free Demo
                </Button>
              </VStack>
            </form>
          </Box>
        </SimpleGrid>
      </Container>
      <Footer />
    </Box>
  );
};

export default Contact; 